import { Evaluation, Group, User } from './models';

/**
 * Unified Invitation System Models
 * Matches the backend unified invitation structure
 */

export enum InvitationType {
  GROUP = 'GROUP',
  USER_RELATION_EVALUATOR = 'USER_RELATION_EVALUATOR',
  USER_RELATION_EVALUATED = 'USER_RELATION_EVALUATED',
  EVALUATION_ANONYMOUS = 'EVALUATION_ANONYMOUS',
}

export enum DeliveryMethod {
  EMAIL = 'EMAIL',
  LINK = 'LINK',
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export interface Invitation {
  id: string;
  type: InvitationType;
  deliveryMethod: DeliveryMethod;
  
  // Token & dates
  randomString: string;
  expireDate: Date | string;
  createdAt: Date | string;
  updatedAt: Date | string;
  sentAt?: Date | string;
  usedAt?: Date | string;
  
  // Status
  isActive: boolean;
  isUsed: boolean;
  
  // Sender/Recipient
  invitedByUserId?: string;
  invitedBy?: User;
  inviteeEmail?: string;
  inviteeUserId?: string;
  invitee?: User;
  inviteeName?: string;
  
  // Target resources (polymorphic)
  groupId?: string;
  group?: Group;
  evaluationId?: string;
  evaluation?: Evaluation;
  evaluatedUserId?: string;
  evaluatedUser?: User;
  
  // Additional
  personalMessage?: string;
  metadata?: Record<string, any>;
  notificationId?: string;
  sendReminder?: boolean;
}

export interface CreateInvitationDto {
  type: InvitationType;
  deliveryMethod: DeliveryMethod;
  
  // Recipient
  inviteeEmail?: string;
  
  // Target resource (provide one based on type)
  groupId?: string;
  evaluationId?: string;
  relatedUserId?: string; // For user relation invitations
  
  // Optional
  expirationDays?: number;
  personalMessage?: string;
  metadata?: Record<string, any>;
  sendReminder?: boolean;
}

export interface AcceptInvitationDto {
  userId?: string;
  data?: any; // Type-specific acceptance data
}

export interface ScoreDto {
  indicatorId: string;
  percentage: number;
  comment?: string;
}

export interface SubmitEvaluationDto {
  invitationToken: string;
  scores: ScoreDto[];
  evaluatorName?: string;
  evaluatorEmail: string;
  selectedIndicatorIds?: string[]; // For unlocked skills - evaluator's chosen indicators
}

export interface GetInvitationsQueryDto {
  type?: InvitationType;
  status?: string; // 'active' | 'used' | 'expired'
  deliveryMethod?: DeliveryMethod;
  page?: number;
  limit?: number;
}

export interface InvitationResponse {
  invitation: Invitation;
  inviteUrl: string;
  emailSent: boolean;
}

export interface PaginatedInvitationsResponse {
  data: Invitation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AcceptResult {
  success: boolean;
  message: string;
  data?: any;
}

export interface SubmitResult {
  success: boolean;
  message: string;
  data?: {
    evaluationScoreId: string;
  };
}

/**
 * Helper type for invitation list filtering
 */
export interface InvitationFilter {
  type?: InvitationType;
  status?: 'active' | 'pending' | 'used' | 'expired' | 'cancelled';
  search?: string;
}

/**
 * UI-specific invitation display model
 */
export interface InvitationDisplay extends Invitation {
  statusLabel: string;
  statusColor: string;
  typeLabel: string;
  actionLabel?: string;
  canResend: boolean;
  canCancel: boolean;
}



